/* eslint-disable @typescript-eslint/naming-convention */
import './styles/style.css'

import type {
	Brain, ChatMessage, User
} from '@types'
import type { ArtifactParams } from '@ui/chat/modules/artifact/Artifact'
import { createContext, useContext, useReducer } from 'react'

import type { ReactPdfViewerParams } from './modules/ReactPdfViewer'

export interface ChatOptions {
	readonly type?: 'chat' | 'chatbot' | undefined;
	readonly displayTips?: boolean | undefined;
	readonly sourcesDisplayType?: 'link' | 'extracts' | undefined;
	readonly displaySources?: boolean | undefined;
	readonly displaySuggestedPrompts?: boolean | undefined;
	readonly displayActions?: boolean | undefined;
	readonly botIcon?: string | undefined;
	readonly color?: string | undefined;
}

export interface IChatContext extends ChatOptions {
	readonly chatState: ChatState;
	readonly chatDispatch: (action: ChatAction) => void;
	readonly user?: User | undefined;
}

interface ChatProps {
	readonly user?: User | undefined;
	readonly children: React.ReactNode | React.ReactNode[];
	readonly options?: ChatOptions | undefined;
}

type ChatAction =
	| {type: 'addMessages'; messages: ChatMessage[]; toStart?: boolean | undefined}
	| {type: 'setMessages'; messages: ChatMessage[]}
	| {type: 'updateOrAddMessage'; message: Partial<ChatMessage> & {id: string}}
	| {type: 'updateMessage'; message: Partial<ChatMessage> & {id: string}}
	| {type: 'deleteMessage'; messageId: string}
	| {type: 'setIsThinkingOrAnswering'; isThinkingOrAnswering: boolean}
	| {type: 'setArtifactParams'; artifactParams?: ArtifactParams | undefined}
	| {type: 'setLoadingMessageId'; loadingMessageId: string}
	| {type: 'setReactPdfViewerParams'; params: ReactPdfViewerParams | undefined}
	| {type: 'setSelectedBrains'; brains: Brain[]}
	| {type: 'setSelectedOpenBrain'; brain: 'chatgpt' | 'perplexity' | undefined}
	| {type: 'setInputValue'; value: string}

type ChatState = {
	readonly messages: ChatMessage[];
	readonly isThinkingOrAnswering: boolean;
	readonly selectedBrains: Brain[];
	readonly inputValue: string;
	readonly selectedOpenBrain?: 'chatgpt' | 'perplexity' | undefined;
	readonly artifactParams?: ArtifactParams | undefined;
	readonly loadingMessageId?: string | undefined;
	readonly reactPdfViewerParams?: ReactPdfViewerParams | undefined;
}

const defaultOptions: ChatOptions = {
	type: 'chat',
	displaySources: true,
	displaySuggestedPrompts: true,
	displayTips: true,
	displayActions: true,
	sourcesDisplayType: 'extracts',
	botIcon: undefined
}

const ChatContext = createContext<IChatContext>({
	...defaultOptions,
	chatState: {
		messages: [],
		inputValue: '',
		isThinkingOrAnswering: false,
		selectedBrains: [],
		selectedOpenBrain: undefined
	},
	chatDispatch() {
		throw new Error('chatDispatch must be implemented')
	}
})

export default function ChatProvider({
	children,
	options,
	user
}: ChatProps) {
	const [chatState, chatStateDispatch] = useReducer(messagesReducer, {
		messages: [],
		isThinkingOrAnswering: false,
		inputValue: '',
		selectedBrains: [],
		selectedOpenBrain: undefined
	})

	const contextValue = {
		...defaultOptions,
		...options,
		chatState,
		chatDispatch: chatStateDispatch
	}

	return (
		<ChatContext.Provider
			value={ {
				...contextValue,
				user
			} }>
			{ children }
		</ChatContext.Provider>
	)
}

function messagesReducer(state: ChatState, action: ChatAction): ChatState {
	switch (action.type) {
		case 'setMessages': {
			return { ...state, messages: action.messages }
		}

		case 'addMessages': {
			const uniqueMessages = action.messages.filter((actionMessage, index, self) =>
				// Check for duplicates within action.messages
				index === self.findIndex(t => t.id === actionMessage.id)
				// Check for duplicates against state.messages
				&& !state.messages.some(stateMessage => stateMessage.id === actionMessage.id))

			if (action.toStart === true) {
				return { ...state, messages: state.messages.length ? [...uniqueMessages, ...state.messages] : uniqueMessages }
			}

			return { ...state, messages: [...state.messages, ...uniqueMessages] }
		}

		case 'updateOrAddMessage': {
			const index = state.messages.findIndex(a => a.id === action.message.id)

			if (index >= 0) {
				state.messages[index] = { ...state.messages[index], ...action.message }

				return state
			}

			return { ...state, messages: [...state.messages, action.message as ChatMessage] }
		}

		case 'updateMessage': {
			const index = state.messages.findIndex(a => a.id === action.message.id)

			if (index !== -1) {
				state.messages[index] = { ...state.messages[index], ...action.message }
			}

			return state
		}

		case 'deleteMessage': {
			return { ...state, messages: state.messages.filter(mes => mes.id !== action.messageId) }
		}

		case 'setIsThinkingOrAnswering': {
			return { ...state, isThinkingOrAnswering: action.isThinkingOrAnswering }
		}

		case 'setArtifactParams': {
			return { ...state, artifactParams: action.artifactParams }
		}

		case 'setLoadingMessageId': {
			return { ...state, loadingMessageId: action.loadingMessageId }
		}

		case 'setReactPdfViewerParams': {
			return { ...state, reactPdfViewerParams: action.params }
		}

		case 'setSelectedBrains': {
			return { ...state, selectedBrains: action.brains }
		}

		case 'setSelectedOpenBrain': {
			return { ...state, selectedOpenBrain: action.brain }
		}

		case 'setInputValue': {
			return { ...state, inputValue: action.value }
		}
	}
}

export function useChatContext() {
	const context = useContext(ChatContext)

	if (!context) {
		throw new Error('useAppContext must be used within a ChatProvider')
	}

	return context
}
